import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const SIZES = {
  sm: 'max-w-[480px]',
  md: 'max-w-[640px]',
  lg: 'max-w-[800px]',
  xl: 'max-w-[960px]',
  '2xl': 'max-w-[1120px]',
}

const Modal = ({
  open,
  title,
  size = 'md',
  compact = false,
  onClose,
  children,
  footer,
  contentClassName = '',
  bodyClassName = '',
}) => {
  const { t } = useTranslation()

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => e.key === 'Escape' && onClose?.()
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null

  // z-[2000]: above admin HeaderBar (1100) — same as ReservationDetails / TwModal
  return createPortal(
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label={t('cartesAcces.shared.modal.close')}
        onClick={onClose}
        className="absolute inset-0 bg-[var(--color-primary-950)]/50 backdrop-blur-sm"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        className={[
          'relative z-10 flex max-h-[92vh] w-full flex-col overflow-hidden rounded-[24px] bg-white shadow-[0_20px_60px_rgba(0,0,0,0.18)] animate-[fadeIn_.2s_ease]',
          SIZES[size],
          contentClassName,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div
          className={[
            'flex items-center justify-between gap-3 border-b border-gray-100',
            compact ? 'px-5 py-2' : 'px-6 py-4',
          ].join(' ')}
        >
          <h2 style={{marginBottom: 0}} className={[compact ? 'text-base' : 'text-lg', 'font-semibold text-gray-900'].join(' ')}>
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('cartesAcces.shared.modal.closeWindow')}
            className={[
              'grid place-items-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-600',
              compact ? 'h-8 w-8' : 'h-9 w-9',
            ].join(' ')}
          >
            <X className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
          </button>
        </div>
        <div
          className={[
            'min-h-0 flex-1',
            bodyClassName ||
              [compact ? 'px-5 py-3' : 'px-6 py-5', 'overflow-y-auto'].join(' '),
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {children}
        </div>
        {footer && (
          <div
            className={[
              'flex items-center justify-end gap-2 border-t border-gray-100',
              compact ? 'px-5 py-2.5' : 'px-6 py-4',
            ].join(' ')}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

export default Modal
